// Environment detection and naming utilities

import type { SxProps, Theme } from '@mui/material/styles';

/** Grey placeholder text for Environments module inputs (scoped on a parent or TextField `sx`). */
export const environmentPlaceholderSx: SxProps<Theme> = {
  '& .MuiInputBase-input::placeholder': {
    color: 'text.disabled',
    opacity: 1,
  },
  '& .MuiInputBase-input::-webkit-input-placeholder': {
    color: 'text.disabled',
    opacity: 1,
  },
  '& textarea.MuiInputBase-input::placeholder': {
    color: 'text.disabled',
    opacity: 1,
  },
};

export interface EnvironmentInfo {
  baseUrl: string;
  name: string;
  environmentType: string;
  description: string;
}

export interface ParsedUrl {
  protocol: string;
  hostname: string;
  port: string;
  pathname: string;
  baseUrl: string;
}

// Parse URL and extract components
export function parseUrl(url: string): ParsedUrl {
  try {
    const urlObj = new URL(url);
    // Base URL should only contain protocol, host, and port
    // Path components (like /api) are part of the API endpoint paths, not the base URL
    let baseUrl = `${urlObj.protocol}//${urlObj.hostname}${urlObj.port ? `:${urlObj.port}` : ''}`;
    
    return {
      protocol: urlObj.protocol,
      hostname: urlObj.hostname,
      port: urlObj.port,
      pathname: urlObj.pathname,
      baseUrl: baseUrl
    };
  } catch (error) {
    throw new Error('Invalid URL format');
  }
}

// Detect environment type from URL patterns
export function detectEnvironmentType(hostname: string): string {
  const lowerHostname = hostname.toLowerCase();
  
  // Development environments
  if (lowerHostname.includes('dev') || lowerHostname.includes('development')) {
    return 'Development';
  }
  
  // Staging environments
  if (lowerHostname.includes('staging') || lowerHostname.includes('stage')) {
    return 'Staging';
  }
  
  // Testing environments
  if (lowerHostname.includes('test') || lowerHostname.includes('qa')) {
    return 'Testing';
  }
  
  // Local development
  if (lowerHostname.includes('localhost') || lowerHostname.includes('127.0.0.1')) {
    return 'Local Development';
  }
  
  // Production (default)
  return 'Production';
}

// Generate user-friendly domain name
export function generateDomainName(hostname: string): string {
  // Remove common prefixes and suffixes
  let domainName = hostname
    .replace(/^(dev-|staging-|test-|api-|www\.)/, '') // Remove common prefixes
    .replace(/\.(com|org|net|io|co|dev)$/, ''); // Remove TLD for cleaner names
  
  // Capitalize and format
  return domainName
    .split('.')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

// Generate environment name
export function generateEnvironmentName(hostname: string, environmentType: string): string {
  const domainName = generateDomainName(hostname);
  return `${domainName} - ${environmentType}`;
}

// Generate environment description
export function generateEnvironmentDescription(hostname: string, environmentType: string, baseUrl: string): string {
  return `Auto-generated environment for ${environmentType.toLowerCase()} API at ${hostname}`;
}

// Main function to analyze URL and generate environment info
export function analyzeUrlForEnvironment(openApiUrl: string): EnvironmentInfo {
  const parsed = parseUrl(openApiUrl);
  const environmentType = detectEnvironmentType(parsed.hostname);
  const name = generateEnvironmentName(parsed.hostname, environmentType);
  const description = generateEnvironmentDescription(parsed.hostname, environmentType, parsed.baseUrl);
  
  return {
    baseUrl: parsed.baseUrl,
    name,
    environmentType,
    description
  };
}

/** Normalize base URL for duplicate checks (lowercase, no trailing slash). */
export function normalizeEnvironmentBaseUrl(url: string | null | undefined): string {
  if (url == null) return '';
  let s = String(url).trim().toLowerCase();
  if (s.endsWith('/')) s = s.slice(0, -1);
  return s;
}

export function environmentBaseUrlMatches(
  swaggerBaseUrl: string,
  env: { baseUrl?: string | null; url?: string | null }
): boolean {
  const swaggerBase = normalizeEnvironmentBaseUrl(swaggerBaseUrl);
  if (!swaggerBase) return false;
  const envUrl = env.baseUrl ?? env.url ?? '';
  return normalizeEnvironmentBaseUrl(envUrl) === swaggerBase;
}

// Examples of environment detection
export const environmentExamples = [
  {
    url: 'https://api.example.com/v3/api-docs',
    expected: { name: 'Example - Production', type: 'Production' }
  },
  {
    url: 'https://dev-api.example.com/swagger',
    expected: { name: 'Example - Development', type: 'Development' }
  },
  {
    url: 'https://staging.example.com/docs',
    expected: { name: 'Example - Staging', type: 'Staging' }
  },
  {
    url: 'http://localhost:8080/api-docs',
    expected: { name: 'Localhost - Local Development', type: 'Local Development' }
  },
  {
    url: 'https://test-api.company.com/openapi',
    expected: { name: 'Company - Testing', type: 'Testing' }
  }
]; 